import { parseXmlDocument } from "./parseTinglysningsDataXml";

type XmlEl = ReturnType<typeof parseXmlDocument>;

export type ServitutDokumentAnmelder = {
   cvr: string;
   name: string;
   phone: string;
   email: string;
};

export type ServitutDokumentRolle = {
   type: string;
   cvr: string;
   name: string;
};

export type ServitutDokumentAnmeldelse = {
   id: string;
   tidspunkt: string;
   ekspeditionstype: string;
};

export type ServitutDokumentParse = {
   isEmpty: boolean;
   emptyMessage: string;
   dokumentId: string;
   revisionNummer: string;
   alias: string;
   /** Root tinglysningsdato: latest relevant filing on this document, not the original. */
   currentTinglysningsDato: string;
   servitutType: string;
   faerdselKode: string;
   tillaegstekster: string[];
   paataleberettiget: ServitutDokumentRolle[];
   anmelder: ServitutDokumentAnmelder | null;
   senesteAnmeldelse: ServitutDokumentAnmeldelse | null;
   hasServitutText: boolean;
   propertyCount: number;
   bilagCount: number;
};

function directChildren(el: XmlEl | undefined, tag: string): XmlEl[] {
   return el?.children.filter((child) => child.tag === tag) ?? [];
}

function firstDirect(el: XmlEl | undefined, tag: string): XmlEl | undefined {
   return directChildren(el, tag)[0];
}

function firstDescendant(el: XmlEl | undefined, tag: string): XmlEl | undefined {
   if (!el) {
      return undefined;
   }
   if (el.tag === tag) {
      return el;
   }
   for (const child of el.children) {
      const found = firstDescendant(child, tag);
      if (found) {
         return found;
      }
   }
   return undefined;
}

function textOf(el: XmlEl | undefined, tag?: string): string {
   const target = tag ? firstDescendant(el, tag) : el;
   return (target?.text ?? "").trim();
}

function countDescendants(el: XmlEl | undefined, tag: string): number {
   if (!el) {
      return 0;
   }
   let count = el.tag === tag ? 1 : 0;
   for (const child of el.children) {
      count += countDescendants(child, tag);
   }
   return count;
}

function collectTillaegstekster(root: XmlEl): string[] {
   const samling = firstDirect(root, "TillaegstekstSamling");
   return samling ? collectTagTexts(samling, "Afsnit") : [];
}

function collectTagTexts(el: XmlEl, tag: string): string[] {
   const texts: string[] = [];
   const walk = (node: XmlEl) => {
      if (node.tag === tag) {
         const value = node.text.trim();
         if (value) {
            texts.push(value);
         }
         return;
      }
      node.children.forEach(walk);
   };
   walk(el);
   return texts;
}

function normalizeRoleRef(value?: string | null): string {
   return (value || "").trim().replace(/^#/, "");
}

function parseSnapshotNames(root: XmlEl): Map<string, string> {
   const names = new Map<string, string>();
   const snapshot = firstDescendant(root, "SnapshotSamling");
   if (!snapshot) {
      return names;
   }
   const walk = (node: XmlEl) => {
      if (node.tag === "RolleStruktur") {
         const name = textOf(firstDirect(node, "Navn")) || textOf(node, "LegalUnitName");
         const cvr = textOf(node, "CVRnumberIdentifier");
         if (name && cvr) {
            names.set(`cvr:${cvr}`, name);
         }
         for (const ref of directChildren(node, "RolleReference")) {
            const id = normalizeRoleRef(ref.text);
            if (id && name) {
               names.set(id, name);
            }
         }
         return;
      }
      node.children.forEach(walk);
   };
   walk(snapshot);
   return names;
}

function parseAnmelder(info: XmlEl | undefined, names: Map<string, string>): ServitutDokumentAnmelder | null {
   if (!info) {
      return null;
   }
   const roleId = normalizeRoleRef(info.attrs.id);
   const cvr = textOf(info, "CVRnumberIdentifier");
   const snapshotName = (roleId && names.get(roleId)) || (cvr && names.get(`cvr:${cvr}`)) || "";
   const anmelder: ServitutDokumentAnmelder = {
      cvr,
      name: snapshotName || textOf(info, "PersonName"),
      phone: textOf(info, "TelephoneNumberIdentifier"),
      email: textOf(info, "EmailAddressIdentifier"),
   };
   if (!anmelder.cvr && !anmelder.name) {
      return null;
   }
   return anmelder;
}

function parseRoller(info: XmlEl | undefined, names: Map<string, string>): ServitutDokumentRolle[] {
   return directChildren(firstDirect(info, "RolleSamling"), "Rolle")
      .map((rolle) => {
         const cvr = textOf(rolle, "CVRnumberIdentifier");
         const roleId = normalizeRoleRef(rolle.attrs.id);
         return {
            type: textOf(firstDirect(rolle, "RolleTypeIdentifikator")),
            cvr,
            name: (roleId && names.get(roleId))
               || (cvr && names.get(`cvr:${cvr}`))
               || textOf(rolle, "LegalUnitName")
               || textOf(rolle, "Navn")
               || "",
         };
      })
      .filter((rolle) => rolle.type || rolle.cvr || rolle.name);
}

function parseSenesteAnmeldelse(root: XmlEl): ServitutDokumentAnmeldelse | null {
   const svar = firstDirect(root, "SenesteAnmeldelseSvar");
   if (!svar) {
      return null;
   }
   const anmeldelse: ServitutDokumentAnmeldelse = {
      id: textOf(firstDirect(svar, "AnmeldelseIdentifikator")),
      tidspunkt: textOf(firstDirect(svar, "AnmeldelsesTidspunkt")),
      ekspeditionstype: textOf(svar, "EkspeditionstypeIdentifikator"),
   };
   if (!anmeldelse.id && !anmeldelse.tidspunkt) {
      return null;
   }
   return anmeldelse;
}

export function emptyServitutDokumentParse(): ServitutDokumentParse {
   return {
      isEmpty: true,
      emptyMessage: "",
      dokumentId: "",
      revisionNummer: "",
      alias: "",
      currentTinglysningsDato: "",
      servitutType: "",
      faerdselKode: "",
      tillaegstekster: [],
      paataleberettiget: [],
      anmelder: null,
      senesteAnmeldelse: null,
      hasServitutText: false,
      propertyCount: 0,
      bilagCount: 0,
   };
}

function xmlInputToText(input: Document | string | null | undefined): string {
   if (!input) {
      return "";
   }
   if (typeof input === "string") {
      return input;
   }
   if (typeof XMLSerializer !== "undefined") {
      return new XMLSerializer().serializeToString(input);
   }
   return input.documentElement?.outerHTML || "";
}

function parseServitutDokumentTree(root: XmlEl): ServitutDokumentParse {
   const empty = emptyServitutDokumentParse();
   if (!root.children.length) {
      return {
         ...empty,
         emptyMessage: root.text.trim(),
      };
   }

   const revision = firstDirect(root, "DokumentRevisionIdentifikator");
   const aliasEl = firstDirect(root, "DokumentAlias");
   const servitut = firstDescendant(root, "Servitut");
   const anmeldelseInfo = firstDirect(root, "ElektroniskaktAnmeldelseInformation");
   const snapshotNames = parseSnapshotNames(root);
   const roller = parseRoller(anmeldelseInfo, snapshotNames);
   const bilagSamling = firstDirect(anmeldelseInfo, "BilagReferenceSamling")
      ?? firstDirect(root, "BilagReferenceSamling");

   return {
      isEmpty: false,
      emptyMessage: "",
      dokumentId: textOf(revision, "DokumentIdentifikator"),
      revisionNummer: textOf(revision, "RevisionNummer"),
      alias: textOf(aliasEl, "DokumentAliasIdentifikator") || textOf(aliasEl, "AktHistoriskIdentifikator"),
      currentTinglysningsDato: textOf(firstDirect(root, "TinglysningsDato")),
      servitutType: textOf(servitut, "ServitutType"),
      faerdselKode: textOf(servitut, "ServitutIndholdFaerdselKode"),
      tillaegstekster: collectTillaegstekster(root),
      paataleberettiget: roller.filter((rolle) => rolle.type === "paataleberettiget"),
      anmelder: parseAnmelder(firstDirect(anmeldelseInfo, "AnmelderInformation"), snapshotNames),
      senesteAnmeldelse: parseSenesteAnmeldelse(root),
      hasServitutText: countDescendants(servitut, "TekstGruppe") > 0,
      propertyCount: countDescendants(root, "FastEjendomMedRettigheder"),
      bilagCount: directChildren(bilagSamling, "BilagReference").length,
   };
}

/**
 * Reads the current tingbog document snapshot (`DokumentAktuelHent`).
 * That snapshot is the latest relevant revision: original servitut text is kept,
 * while dates, anmelder and bilag belong to the current filing.
 * `TinglysningspaategningElement` is the official stamp on properties, not the påtegning list.
 */
function isXmlDocument(input: Document | string | null | undefined): input is Document {
   return Boolean(input && typeof input !== "string" && typeof (input as Document).getElementsByTagName === "function");
}

function firstElementText(el: Element | null | undefined, tag: string): string {
   return (el?.getElementsByTagName(tag)[0]?.textContent || "").trim();
}

function enrichPartiesFromDom(parsed: ServitutDokumentParse, xmlDoc: Document): ServitutDokumentParse {
   const namesByRef = new Map<string, string>();
   const namesByCvr = new Map<string, string>();
   const snapshot = xmlDoc.getElementsByTagName("SnapshotSamling")[0];
   if (snapshot) {
      Array.from(snapshot.getElementsByTagName("RolleStruktur")).forEach((role) => {
         const name = firstElementText(role, "Navn") || firstElementText(role, "LegalUnitName");
         const cvr = firstElementText(role, "CVRnumberIdentifier");
         if (name && cvr) {
            namesByCvr.set(cvr, name);
         }
         Array.from(role.getElementsByTagName("RolleReference")).forEach((refEl) => {
            const ref = normalizeRoleRef(refEl.textContent);
            if (ref && name) {
               namesByRef.set(ref, name);
            }
         });
      });
   }

   const paataleberettiget = parsed.paataleberettiget.map((rolle) => ({
      ...rolle,
      name: rolle.name || namesByCvr.get(rolle.cvr) || rolle.name,
   }));

   const anmeldelse = xmlDoc.getElementsByTagName("ElektroniskaktAnmeldelseInformation")[0];
   if (anmeldelse) {
      Array.from(anmeldelse.getElementsByTagName("Rolle")).forEach((role) => {
         if (firstElementText(role, "RolleTypeIdentifikator") !== "paataleberettiget") {
            return;
         }
         const cvr = firstElementText(role, "CVRnumberIdentifier");
         const roleId = normalizeRoleRef(role.getAttribute("id") || role.getAttribute("Id"));
         const name = namesByRef.get(roleId) || namesByCvr.get(cvr) || "";
         const target = paataleberettiget.find((item) => item.cvr === cvr)
            || paataleberettiget.find((item) => !item.name);
         if (target && name) {
            target.name = target.name || name;
         } else if (name || cvr) {
            paataleberettiget.push({ type: "paataleberettiget", cvr, name });
         }
      });
   }

   let anmelder = parsed.anmelder;
   if (anmelder && !anmelder.name) {
      const info = xmlDoc.getElementsByTagName("AnmelderInformation")[0];
      const roleId = normalizeRoleRef(info?.getAttribute("id") || info?.getAttribute("Id"));
      anmelder = {
         ...anmelder,
         name: namesByRef.get(roleId) || namesByCvr.get(anmelder.cvr) || anmelder.name,
      };
   }

   return { ...parsed, paataleberettiget, anmelder };
}

export function parseServitutDokumentXml(input: Document | string | null | undefined): ServitutDokumentParse {
   const xml = xmlInputToText(input).trim();
   if (!xml) {
      return emptyServitutDokumentParse();
   }
   try {
      const parsed = parseServitutDokumentTree(parseXmlDocument(xml));
      return isXmlDocument(input) ? enrichPartiesFromDom(parsed, input) : parsed;
   } catch {
      return emptyServitutDokumentParse();
   }
}

export function formatServitutTypeLabel(type?: string): string {
   if (!type) {
      return "";
   }
   if (type === "andenServitut") {
      return "Servitut";
   }
   if (type === "lejekontrakt") {
      return "Lejekontrakt";
   }
   return type.charAt(0).toUpperCase() + type.slice(1);
}

export function formatPartyDisplay(party?: { name?: string; legalUnitName?: string; cvr?: string; cvrNummer?: string } | null): string {
   const name = (party?.name || party?.legalUnitName || "").trim();
   const cvr = (party?.cvr || party?.cvrNummer || "").trim();
   if (name && cvr) {
      return `${name} (CVR ${cvr})`;
   }
   return name || (cvr ? `CVR ${cvr}` : "");
}

export function collectPaataleberettigetLabels(
   parsed?: ServitutDokumentParse | null,
   extras: Array<{ name?: string; legalUnitName?: string; cvr?: string; cvrNummer?: string } | null | undefined> = [],
): string[] {
   const merged = new Map<string, { name: string; cvr: string }>();
   const add = (party?: { name?: string; legalUnitName?: string; cvr?: string; cvrNummer?: string } | null) => {
      if (!party) {
         return;
      }
      const name = (party.name || party.legalUnitName || "").trim();
      const cvr = (party.cvr || party.cvrNummer || "").trim();
      if (!name && !cvr) {
         return;
      }
      const key = cvr || name.toLowerCase();
      const existing = merged.get(key);
      if (!existing) {
         merged.set(key, { name, cvr });
         return;
      }
      if (!existing.name && name) {
         existing.name = name;
      }
      if (!existing.cvr && cvr) {
         existing.cvr = cvr;
      }
   };
   (parsed?.paataleberettiget || []).forEach(add);
   extras.forEach(add);
   return [...merged.values()].map((party) => formatPartyDisplay(party)).filter(Boolean);
}

export function formatFaerdselLabel(kode?: string): string {
   if (!kode) {
      return "";
   }
   if (kode === "vej") {
      return "Vej";
   }
   return kode.charAt(0).toUpperCase() + kode.slice(1);
}
